import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";

const API_BASE = `${BACKEND_URL}/payments`;

/**
 * Meios de pagamento da empresa (PaymentMethodController - /payments)
 * PaymentDTO: { id, name, icon, active }. O `id` é a CHAVE (texto, única por
 * empresa) e é o valor enviado em `payMethod` no pedido.
 * Erros: PAY003 chave duplicada, PAY005 usado em pedidos (não exclui),
 * PAY006 não troca a chave de meio usado em pedidos, PAY002 não encontrado.
 */
class PaymentService {
  /**
   * Meios da empresa, do mais antigo ao mais novo (ordem fixa da API).
   * Empresa sem meios recebe 404 (PAY004): aqui vira [] sem abrir o modal.
   * @param {object} [params] - { active: true | false } (sem = todos)
   * @param {object} [options] - config extra do axios (ex.: { silent: true })
   * @returns {Promise<Array<{id, name, icon, active}>>}
   */
  async getAll({ active } = {}, options = {}) {
    try {
      const response = await api.get(API_BASE, {
        params: typeof active === "boolean" ? { active } : undefined,
        silentStatuses: [404],
        ...options,
      });
      return unwrapList(response);
    } catch (error) {
      if (error.response?.status === 404) return [];
      console.error("❌ Erro ao buscar meios de pagamento:", error);
      throw error;
    }
  }

  /**
   * @param {string} key
   * @returns {Promise<object|null>}
   */
  async getById(key) {
    try {
      const response = await api.get(`${API_BASE}/${encodeURIComponent(key)}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar meio de pagamento ${key}:`, error);
      throw error;
    }
  }

  /**
   * @param {{id, name, icon, active}} payment
   * @param {object} [options] - config extra do axios
   */
  async create(payment, options = {}) {
    try {
      const response = await api.post(API_BASE, payment, options);
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar meio de pagamento:", error);
      throw error;
    }
  }

  /**
   * @param {string} key - chave atual (o payload pode trazer uma chave nova)
   * @param {{id, name, icon, active}} payment
   * @param {object} [options] - config extra do axios
   */
  async update(key, payment, options = {}) {
    try {
      const response = await api.put(`${API_BASE}/${encodeURIComponent(key)}`, payment, options);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar meio de pagamento ${key}:`, error);
      throw error;
    }
  }

  /**
   * Ativa/desativa (PUT /{key}/activate | /{key}/deactivate)
   * @param {string} key
   * @param {boolean} active
   */
  async setActive(key, active) {
    try {
      const action = active ? "activate" : "deactivate";
      const response = await api.put(`${API_BASE}/${encodeURIComponent(key)}/${action}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao ${active ? "ativar" : "desativar"} meio de pagamento ${key}:`, error);
      throw error;
    }
  }

  /**
   * Só exclui meio nunca usado em pedidos (senão PAY005: desative-o)
   * @param {string} key
   * @param {object} [options] - config extra do axios
   */
  async delete(key, options = {}) {
    try {
      const response = await api.delete(`${API_BASE}/${encodeURIComponent(key)}`, options);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao excluir meio de pagamento ${key}:`, error);
      throw error;
    }
  }

  /**
   * Nomes dos meios ATIVOS (para selects de pay_method)
   * @returns {Promise<string[]>}
   */
  async getNames() {
    const data = await this.getAll({ active: true });
    return data.map((p) => p?.name).filter(Boolean);
  }
}

export default new PaymentService();
